import React, { useState, useRef } from "react";
import { useClickOutside } from "../hooks/useClickOutside";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import AvatarPlaceholder from "../assets/images/avatar-placeholder.svg";
import { isLifetimeOgUser, isOgUser } from "utils/roles";
import {
  Crown,
  Star,
  ChevronDown,
  UserPlus,
  Headphones,
  Settings,
  LogOut,
} from "lucide-react";

interface DropdownItem {
  label: string;
  icon: string;
  onClick: () => void;
}

interface ProfileDropdownProps {
  userAvatar: string;
  items: DropdownItem[];
  badge?: React.ReactNode;
}

const DefaultAvatarBox: React.FC<{ className?: string }> = ({
  className = "w-10 h-10",
}) => (
  <div
    className={`${className} rounded-[16px] bg-[#E5E7EB] flex items-center justify-center shrink-0 overflow-hidden`}
  >
    <svg
      viewBox="0 0 64 64"
      className="w-full h-full"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="64" height="64" rx="16" fill="#E5E7EB" />
      <circle cx="32" cy="25" r="11" fill="#9CA3AF" />
      <ellipse cx="32" cy="56" rx="19" ry="16" fill="#9CA3AF" />
    </svg>
  </div>
);

const getItemIcon = (label: string, fallbackIcon: string) => {
  const l = label.toLowerCase();
  if (l.includes("invite")) {
    return <UserPlus className="w-4 h-4 text-gray-700 group-hover:text-gray-900 shrink-0" />;
  }
  if (l.includes("contact")) {
    return <Headphones className="w-4 h-4 text-gray-700 group-hover:text-gray-900 shrink-0" />;
  }
  if (l.includes("settings")) {
    return <Settings className="w-4 h-4 text-gray-700 group-hover:text-gray-900 shrink-0" />;
  }
  return <i className={`${fallbackIcon} text-gray-700 text-base shrink-0`} />;
};

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  userAvatar,
  items,
}) => {
  const { username, role, resetUserDetails } = useUserStore((state) => state);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [imgError, setImgError] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useStore();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const logoutFunction = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      logout();
      resetUserDetails();
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const isOg = isLifetimeOgUser(role) || isOgUser(role);

  return (
    <div className="relative shrink-0 text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={toggleDropdown}
        className="h-12 flex items-center gap-1.5 sm:gap-3 p-1 pl-1 pr-2.5 sm:p-1.5 sm:pl-1.5 sm:pr-4 rounded-[20px] bg-[#EFEFEF] hover:bg-[#E5E5E5] border border-gray-300/50 transition-colors shrink-0 focus:outline-none cursor-pointer"
        type="button"
      >
        <div className="relative shrink-0 w-10 h-10 flex items-center justify-center">
          {userAvatar && !imgError ? (
            <img
              src={userAvatar}
              alt="User Avatar"
              className="w-10 h-10 rounded-[16px] object-cover shrink-0"
              onError={() => setImgError(true)}
            />
          ) : (
            <DefaultAvatarBox className="w-10 h-10" />
          )}
          {isOg && (
            <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#FFB800] border-2 border-[#EFEFEF] flex items-center justify-center shadow-sm z-10">
              <Crown className="w-3.5 h-3.5 text-black fill-black" />
            </span>
          )}
        </div>

        <span className="hidden sm:block text-base text-black font-bold leading-none whitespace-nowrap truncate max-w-[140px] sm:max-w-[180px]">
          {username || "Alex meian"}
        </span>

        <ChevronDown
          className={`w-4 h-4 text-black stroke-[2.5] transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-1 w-[195px] bg-white rounded-[14px] shadow-2xl border border-gray-100 overflow-hidden z-50 text-left">
          {/* Header */}
          <div className="bg-[#192227] px-3 py-3 flex items-center gap-2">
            {userAvatar && !imgError ? (
              <img
                src={userAvatar}
                alt="User Avatar"
                className="w-9 h-9 rounded-[9px] object-cover shrink-0"
                onError={() => setImgError(true)}
              />
            ) : (
              <DefaultAvatarBox className="w-9 h-9 rounded-[9px]" />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm tracking-tight truncate">
                {username || "Alex meian"}
              </h3>
              {isLifetimeOgUser(role) && (
                <span className="mt-1 inline-flex items-center gap-1 bg-[#FFB800] text-black text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  <Crown className="w-2.5 h-2.5 fill-black text-black" /> Lifetime OG
                </span>
              )}
              {isOgUser(role) && (
                <span className="mt-1 inline-flex items-center gap-1 bg-[#FFB800] text-black text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  <Star className="w-2.5 h-2.5 fill-black text-black" /> OG User
                </span>
              )}
              {!isLifetimeOgUser(role) && !isOgUser(role) && role && (
                <span className="mt-1 inline-flex items-center gap-1 bg-[#FFB800] text-black text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  {role}
                </span>
              )}
            </div>
          </div>

          {/* Body Menu Items */}
          <div className="px-2.5 pt-2.5 pb-2.5 bg-white">
            {items.map((item, index) => {
              const isSettings = item.label.toLowerCase().includes("settings");
              return (
                <React.Fragment key={index}>
                  {isSettings && <div className="my-1.5 border-t border-gray-100" />}
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-1 py-1.5 rounded-lg hover:bg-gray-100/70 transition-colors text-left group"
                    onClick={() => {
                      item.onClick();
                      setIsOpen(false);
                    }}
                  >
                    {getItemIcon(item.label, item.icon)}
                    <span className="text-sm font-semibold text-gray-900">
                      {item.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}

            {/* Log Out Button */}
            <button
              type="button"
              onClick={logoutFunction}
              className="w-full mt-2 h-8 px-3 rounded-md font-semibold text-sm flex items-center justify-center gap-2 border border-red-400 bg-red-500/10 text-[#192227] hover:bg-red-500/20 transition-colors"
            >
              <LogOut className="w-4 h-4 stroke-[2.2]" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
